import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, FileText, Hammer, Loader2, Plus, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { LinkText } from '@/components/LinkText';
import {
  MigratedCrudHistory,
  formatMigratedDateTime,
} from '@/components/MigratedCrudHistory';
import { clientsApi, type ClientForSelector } from '@/api/clients';
import {
  subcontractorWorksApi,
  type SaveSubcontractorWorkDto,
  type SubcontractorVendor,
  type SubcontractorWork,
} from '@/api/subcontractor-works';

type Status = 'active' | 'archived' | 'all';

const STATUS_OPTIONS: Array<{ value: Status; label: string }> = [
  { value: 'active', label: 'Активные' },
  { value: 'archived', label: 'Архив' },
  { value: 'all', label: 'Все' },
];

const FIELD_LABELS: Record<string, string> = {
  clientName: 'Клиент',
  workName: 'Работа',
  vendorName: 'Подрядчик',
  vendorCost: 'Сумма подрядчика',
  clientCost: 'Сумма клиенту',
  clientInvoice: 'Счет ДС',
  vendorDone: 'Работы выполнены',
  comment: 'Комментарий',
  vendorPayment: 'Оплата подрядчику',
  vendorDocsReceived: 'Документы получены',
  vendorDocsFinished: 'Документы закрыты',
  isArchived: 'Архив',
};

function boolText(value: boolean): string {
  return value ? 'Да' : 'Нет';
}

function WorkCard({ entry, onClick }: { entry: SubcontractorWork; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full rounded-lg border border-border bg-card p-3 text-left transition-colors hover:bg-accent active:bg-accent"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-sm font-medium">{entry.workName}</h3>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">
            {entry.clientName}
            {(entry.vendor?.name || entry.vendorName) && ` -> ${entry.vendor?.name || entry.vendorName}`}
          </p>
        </div>
        <span className={`rounded px-2 py-1 text-xs ${entry.isArchived ? 'bg-muted text-muted-foreground' : entry.vendorDone ? 'bg-emerald-500/10 text-emerald-700' : 'bg-amber-500/10 text-amber-700'}`}>
          {entry.isArchived ? 'Архив' : entry.vendorDone ? 'Выполнено' : 'В работе'}
        </span>
      </div>

      <div className="mt-2 space-y-1 text-xs text-muted-foreground">
        <div className="flex flex-wrap gap-x-3 gap-y-0.5">
          <span>Подрядчик: {entry.vendorCost || '-'}</span>
          <span>Клиенту: {entry.clientCost || '-'}</span>
          <span>Оплата: {boolText(entry.vendorPayment)}</span>
          <span>Док-ты: {boolText(entry.vendorDocsReceived)}</span>
        </div>
        {entry.clientInvoice && <div>Счет: {entry.clientInvoice}</div>}
        {entry.comment && <LinkText>{entry.comment}</LinkText>}
      </div>
    </button>
  );
}

function FileButton({
  entry,
  kind,
  label,
  fileName,
}: {
  entry: SubcontractorWork;
  kind: 'offer' | 'vendor-docs';
  label: string;
  fileName: string | null;
}) {
  const [loading, setLoading] = useState(false);

  if (!fileName) return null;

  const openFile = async () => {
    setLoading(true);
    try {
      const url = await subcontractorWorksApi.getFileBlobUrl(entry.id, kind);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      toast.error('Не удалось открыть файл');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button type="button" variant="outline" size="sm" onClick={openFile} disabled={loading}>
      {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />}
      {label}: {fileName}
    </Button>
  );
}

function WorkSheet({
  entry,
  vendors,
  open,
  onOpenChange,
}: {
  entry: Partial<SubcontractorWork> | null;
  vendors: SubcontractorVendor[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const isEditing = Boolean(entry?.id);
  const [clientId, setClientId] = useState<number | undefined>(entry?.clientId ?? undefined);
  const [workName, setWorkName] = useState(entry?.workName || '');
  const [vendorId, setVendorId] = useState(entry?.vendorId || '');
  const [vendorCost, setVendorCost] = useState(entry?.vendorCost || '');
  const [clientCost, setClientCost] = useState(entry?.clientCost || '');
  const [clientInvoice, setClientInvoice] = useState(entry?.clientInvoice || '');
  const [vendorDone, setVendorDone] = useState(entry?.vendorDone || false);
  const [comment, setComment] = useState(entry?.comment || '');
  const [vendorPayment, setVendorPayment] = useState(entry?.vendorPayment || false);
  const [vendorDocsReceived, setVendorDocsReceived] = useState(entry?.vendorDocsReceived || false);
  const [vendorDocsFinished, setVendorDocsFinished] = useState(entry?.vendorDocsFinished || false);
  const [isArchived, setIsArchived] = useState(entry?.isArchived || false);
  const [offerFile, setOfferFile] = useState<File | null>(null);
  const [vendorDocsFile, setVendorDocsFile] = useState<File | null>(null);

  const { data: clients = [] } = useQuery({
    queryKey: ['clients-for-selector', 'subcontractor-works'],
    queryFn: () => clientsApi.getForSelector('all'),
  });

  const clientOptions = useMemo<ClientForSelector[]>(() => {
    if (!clientId || clients.some((client) => client.id === clientId)) {
      return clients;
    }

    return [
      {
        id: clientId,
        name: entry?.clientName?.trim() || `Клиент #${clientId}`,
      },
      ...clients,
    ];
  }, [clientId, clients, entry?.clientName]);

  const historyQuery = useQuery({
    queryKey: ['subcontractor-work-history', entry?.id],
    queryFn: () => subcontractorWorksApi.getHistory(entry!.id!),
    enabled: open && !!entry?.id,
  });

  const saveMutation = useMutation({
    mutationFn: ({
      dto,
      files,
    }: {
      dto: SaveSubcontractorWorkDto;
      files?: { offerFile?: File | null; vendorDocsFile?: File | null };
    }) => subcontractorWorksApi.save(dto, files),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['subcontractor-works'] });
      toast.success(data.action === 'created' ? 'Работа создана' : 'Работа обновлена');
      onOpenChange(false);
    },
    onError: () => toast.error('Ошибка сохранения'),
  });

  useEffect(() => {
    setClientId(entry?.clientId ?? undefined);
    setWorkName(entry?.workName || '');
    setVendorId(entry?.vendorId || '');
    setVendorCost(entry?.vendorCost || '');
    setClientCost(entry?.clientCost || '');
    setClientInvoice(entry?.clientInvoice || '');
    setVendorDone(entry?.vendorDone || false);
    setComment(entry?.comment || '');
    setVendorPayment(entry?.vendorPayment || false);
    setVendorDocsReceived(entry?.vendorDocsReceived || false);
    setVendorDocsFinished(entry?.vendorDocsFinished || false);
    setIsArchived(entry?.isArchived || false);
    setOfferFile(null);
    setVendorDocsFile(null);
  }, [entry]);

  const handleSave = () => {
    if (!clientId || !workName.trim()) return;
    saveMutation.mutate({
      dto: {
        id: entry?.id,
        clientId,
        workName: workName.trim(),
        vendorId: vendorId || undefined,
        vendorCost: vendorCost.trim() || undefined,
        clientCost: clientCost.trim() || undefined,
        clientInvoice: clientInvoice.trim() || undefined,
        vendorDone,
        comment: comment.trim() || undefined,
        vendorPayment,
        vendorDocsReceived,
        vendorDocsFinished,
        isArchived,
      },
      files: { offerFile, vendorDocsFile },
    });
  };

  const canSave = Boolean(clientId && workName.trim() && !saveMutation.isPending);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[92vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{isEditing ? 'Работа субподрядчика' : 'Новая работа'}</SheetTitle>
        </SheetHeader>

        <div className="mt-4 space-y-4 pb-4">
          <div>
            <Label>Клиент *</Label>
            <Select
              value={clientId ? String(clientId) : ''}
              onValueChange={(value) => setClientId(Number(value))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Выберите клиента" />
              </SelectTrigger>
              <SelectContent>
                {clientOptions.map((client) => (
                  <SelectItem key={client.id} value={String(client.id)}>
                    {client.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="workName">Работа *</Label>
            <Textarea
              id="workName"
              value={workName}
              onChange={(event) => setWorkName(event.target.value)}
              rows={3}
            />
          </div>

          <div>
            <Label>Подрядчик</Label>
            <Select value={vendorId} onValueChange={setVendorId}>
              <SelectTrigger>
                <SelectValue placeholder="Выберите подрядчика" />
              </SelectTrigger>
              <SelectContent>
                {vendors.map((vendor) => (
                  <SelectItem key={vendor.id} value={vendor.id}>
                    {vendor.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="vendorCost">Подрядчик</Label>
              <Input
                id="vendorCost"
                value={vendorCost}
                onChange={(event) => setVendorCost(event.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="clientCost">Клиенту</Label>
              <Input
                id="clientCost"
                value={clientCost}
                onChange={(event) => setClientCost(event.target.value)}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="clientInvoice">Счет ДС</Label>
            <Input
              id="clientInvoice"
              value={clientInvoice}
              onChange={(event) => setClientInvoice(event.target.value)}
            />
          </div>

          <div className="space-y-3 border-t pt-4">
            <div>
              <Label htmlFor="offerFile">Оферта</Label>
              <Input
                id="offerFile"
                type="file"
                onChange={(event) => setOfferFile(event.target.files?.[0] || null)}
              />
            </div>
            <div>
              <Label htmlFor="vendorDocsFile">Документы подрядчика</Label>
              <Input
                id="vendorDocsFile"
                type="file"
                onChange={(event) => setVendorDocsFile(event.target.files?.[0] || null)}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="comment">Комментарий</Label>
            <Textarea
              id="comment"
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              rows={3}
            />
          </div>

          <div className="space-y-3 border-t pt-4">
            <div className="flex items-center space-x-2">
              <Checkbox id="vendorDone" checked={vendorDone} onCheckedChange={(checked) => setVendorDone(checked === true)} />
              <Label htmlFor="vendorDone" className="font-normal">Работы выполнены</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox id="vendorPayment" checked={vendorPayment} onCheckedChange={(checked) => setVendorPayment(checked === true)} />
              <Label htmlFor="vendorPayment" className="font-normal">Оплата подрядчику</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox id="vendorDocsReceived" checked={vendorDocsReceived} onCheckedChange={(checked) => setVendorDocsReceived(checked === true)} />
              <Label htmlFor="vendorDocsReceived" className="font-normal">Документы получены</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox id="vendorDocsFinished" checked={vendorDocsFinished} onCheckedChange={(checked) => setVendorDocsFinished(checked === true)} />
              <Label htmlFor="vendorDocsFinished" className="font-normal">Документы закрыты</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox id="isArchived" checked={isArchived} onCheckedChange={(checked) => setIsArchived(checked === true)} />
              <Label htmlFor="isArchived" className="font-normal">Архив</Label>
            </div>
          </div>

          {entry?.id && (
            <div className="space-y-2 border-t pt-4">
              <FileButton
                entry={entry as SubcontractorWork}
                kind="offer"
                label="Оферта"
                fileName={entry.offerFileName || null}
              />
              <FileButton
                entry={entry as SubcontractorWork}
                kind="vendor-docs"
                label="Документы"
                fileName={entry.vendorDocsFileName || null}
              />
              <div className="text-xs text-muted-foreground">
                Legacy ID: {entry.legacyId || '-'} · Изменено: {formatMigratedDateTime(entry.legacyLastChange || entry.updatedAt)}
              </div>
            </div>
          )}

          {entry?.id && (
            <div className="border-t pt-4">
              <MigratedCrudHistory
                legacyLog={entry.legacyLog}
                auditItems={historyQuery.data?.items || []}
                total={historyQuery.data?.total}
                isLoading={historyQuery.isLoading}
                error={historyQuery.error}
                fieldLabels={FIELD_LABELS}
              />
            </div>
          )}

          <Button onClick={handleSave} disabled={!canSave} className="w-full">
            {saveMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Сохранение...
              </>
            ) : (
              'Сохранить'
            )}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function SubcontractorWorksPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState<Status>('active');
  const { data, isLoading, error } = useQuery({
    queryKey: ['subcontractor-works', status],
    queryFn: () => subcontractorWorksApi.getAll(status),
    staleTime: 60_000,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEntry, setSelectedEntry] = useState<Partial<SubcontractorWork> | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const prefilledClientId = searchParams.get('clientId');
  const prefilledClient = searchParams.get('client');

  useEffect(() => {
    if (!prefilledClientId) return;

    const parsedClientId = Number(prefilledClientId);
    if (!Number.isFinite(parsedClientId)) return;

    setSelectedEntry({
      clientId: parsedClientId,
      clientName: prefilledClient || '',
    });
    setSheetOpen(true);
    setSearchParams({}, { replace: true });
  }, [prefilledClient, prefilledClientId, setSearchParams]);

  const filteredEntries = useMemo(() => {
    const entries = data?.entries || [];
    if (!searchQuery) return entries;
    const query = searchQuery.toLowerCase();
    return entries.filter((entry) =>
      entry.workName.toLowerCase().includes(query) ||
      entry.clientName.toLowerCase().includes(query) ||
      entry.vendor?.name?.toLowerCase().includes(query) ||
      entry.vendorName?.toLowerCase().includes(query) ||
      entry.comment?.toLowerCase().includes(query),
    );
  }, [data?.entries, searchQuery]);

  const handleAddNew = () => {
    setSelectedEntry({});
    setSheetOpen(true);
  };

  const handleSheetClose = (open: boolean) => {
    setSheetOpen(open);
    if (!open) setSelectedEntry(null);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header
        title="Работы субподрядчиков"
        showBack
        rightContent={
          <Button variant="ghost" size="icon" onClick={handleAddNew} className="h-9 w-9">
            <Plus className="h-5 w-5" />
          </Button>
        }
      />

      <main className="p-4">
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Поиск по клиенту, подрядчику, работе..."
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            className="pl-10 pr-10"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="mb-4 grid grid-cols-3 gap-1 rounded-lg border border-border p-1">
          {STATUS_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setStatus(option.value)}
              className={`rounded-md px-2 py-1.5 text-sm ${
                status === option.value ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        {!isLoading && !error && data && (
          <p className="mb-3 text-sm text-muted-foreground">
            {searchQuery
              ? `Найдено: ${filteredEntries.length} из ${data.entries.length}`
              : `Всего работ: ${data.entries.length}`}
          </p>
        )}

        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}

        {error && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <AlertCircle className="mb-4 h-12 w-12 text-destructive" />
            <p className="text-muted-foreground">Ошибка загрузки</p>
          </div>
        )}

        {!isLoading && !error && data && filteredEntries.length > 0 && (
          <div className="space-y-2">
            {filteredEntries.map((entry) => (
              <WorkCard
                key={entry.id}
                entry={entry}
                onClick={() => {
                  setSelectedEntry(entry);
                  setSheetOpen(true);
                }}
              />
            ))}
          </div>
        )}

        {!isLoading && !error && data && data.entries.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Hammer className="mb-4 h-12 w-12 text-muted-foreground" />
            <p className="text-muted-foreground">Нет работ субподрядчиков</p>
            <Button variant="outline" className="mt-4" onClick={handleAddNew}>
              <Plus className="mr-2 h-4 w-4" />
              Создать работу
            </Button>
          </div>
        )}
      </main>

      <WorkSheet
        entry={selectedEntry}
        vendors={data?.vendors || []}
        open={sheetOpen}
        onOpenChange={handleSheetClose}
      />
    </div>
  );
}
